<template>
  <div class="contractor-contact-wrapper">
    <table
      v-if="contacts.length > 0"
      class="contractor-contacts"
    >
      <thead>
        <tr>
          <th
            v-for="(header, index) in itemHeaders"
            :key="`contractor_contact_${index}`"
            :class="{'required-flag': isEditing && isPrimary && !header.notRequireWhenPrimary}"
          >
            {{ header.name }}
          </th>
          <th :class="{'icon-remove': isEditing, 'invite-contractor-user': !isEditing}" />
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(contact, index) in contacts"
          :key="`contact_${index}`"
        >
          <td class="contact-name">
            <template v-if="isEditing">
              <InputField
                v-for="fullnameObj of getFullnameSorts"
                :key="`contact_name_${fullnameObj.key}`"
                :required="isEditing && isPrimary"
                :readonly="!isEditing"
                :placeholder="$t(`contact.basic.${fullnameObj.label}`)"
                :value="deep(contact, fullnameObj.value)"
                @updateValue="val => update(getUpdatePath(index), fullnameObj.value, val)"
              />
            </template>
            <span
              v-else
              class=""
            >
              {{ getDisplayName(contact) }}
            </span>
          </td>
          <td
            v-for="(attr, oIndex) in otherFields"
            :key="`other_contact_${oIndex}`"
            class="contact-phone"
          >
            <InputField
              :required="isEditing && isPrimary"
              :readonly="!isEditing"
              :class="{'required-error-highlight': inputRequireHighlight && isSamePhoneNumbers(contact)}"
              :value="deep(contact, attr) || (isEditing ? '' : '--')"
              @updateValue="val => update(getUpdatePath(index), attr, val)"
            />
          </td>
          <td
            class="contact-email"
            :class="{ 'contact-email-editing': isEditing }"
          >
            <InputField
              :required="isEditing && (!!deep(contact, 'email') || (isPrimary && !isSubFromExternal))"
              :readonly="!isEditing"
              :readonlyAsInput="isEditing && isSubFromExternal"
              :inputCheckType="'email'"
              :inputTip="$t('hint.inputTip.email')"
              :pattern="regex.email"
              :value="deep(contact, 'email') || (isEditing ? '' : '--')"
              @updateValue="val => update(getUpdatePath(index), 'email', val)"
            />
          </td>
          <td
            v-if="isEditing"
            class="icon-remove"
          >
            <div
              v-if="isPrimary"
              class="hold-delete"
            />
            <i
              v-else
              class="fa fa-times-thin"
              @click="openDeleteConfirm(index)"
            />
          </td>
          <td
            v-else
            class="invite-contractor-user"
          >
            <a
              v-if="isPrimary && deep(contact, 'email') && canInvite"
              @click="invite()"
            >{{ $t('action.invite') }}</a>
            <div
              v-else
              class="hold-invite"
            />
          </td>
        </tr>
      </tbody>
    </table>
    <div
      v-if="isEditing && !isPrimary && contacts.length <= 1"
      class="add-contact"
      @click="toggleContact(contacts.length)"
    >
      {{ $t('action.addContact') }}+
    </div>
    <ActionConfirmModal
      ref="deleteConfirmModal"
      :modalId="delConfModalId"
      :confirmTitle="$t('deleteConfirmMsg')"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import InputField from '@/controls/InputField'
import formFullnameMixin from '@/pages/formFullnameMixin'
import * as regex from '@/utils/regex'
import { ContactExample } from './consts'

export default {
  name: 'ContractorContact',
  components: {
    InputField,
    ActionConfirmModal,
  },
  mixins: [formFullnameMixin],
  props: {
    updatePath: {
      type: Array,
      default: () => ([]),
    },
    update: {
      type: Function,
      default: () => {},
    },
    toggleContact: {
      type: Function,
      default: () => {},
    },
    invite: {
      type: Function,
      default: () => {},
    },
    canInvite: Boolean,
    isEditing: Boolean,
    isPrimary: Boolean,
    contacts: {
      type: Array,
      default () { return [] },
    },
    inputRequireHighlight: Boolean,
    isSubFromExternal: Boolean,
  },
  data () {
    return {
      delConfModalId: `contractorCont-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`,
      deep,
      regex,
      ContactExample,
    }
  },
  computed: {
    nameFileds () {
      return ['firstName', 'lastName']
    },
    otherFields () {
      return ['altPhone', 'phone']
    },
    itemHeaders () {
      return [{
        name: this.$t('name'),
      }, {
        name: this.$t('officeTel'),
      }, {
        name: this.$t('mobile'),
      }, {
        name: this.$t('contact.email.emailAddr'),
        notRequireWhenPrimary: this.isSubFromExternal,
      }]
    },
  },
  methods: {
    getDisplayName (contact) {
      const [firstName, lastName] = [
        lodash.get(contact, this.nameFileds[0]),
        lodash.get(contact, this.nameFileds[1]),
      ]
      if (firstName || lastName) {
        return this.displayI18nFullName(firstName, lastName)
      }
      return this.$t('emptyValue')
    },
    getUpdatePath (index) {
      return this.isPrimary ? this.updatePath : this.updatePath.concat(index)
    },
    openDeleteConfirm (index) {
      this.$refs.deleteConfirmModal.open(this.toggleContact, [index, true])
    },
    isSamePhoneNumbers (contact) {
      return deep(contact, this.otherFields[0]) !== '' && deep(contact, this.otherFields[0]) === deep(contact, this.otherFields[1])
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/common';
@import '~styles/table';
@import '~styles/multi-value';
@import '~styles/icon_remove';

.contractor-contact-wrapper {
  thead th {
    padding: 10px 20px;
  }
  .contact-name {
    width: 315px;
  }
  .contact-email-editing {
    padding-right: 10px;
  }
  .icon-remove {
    padding-left: 0;
    width: 40px;
    .fa.fa-times-thin::before {
      font-size: 32px;
      vertical-align: text-bottom;
    }
  }
  .td-circle-add {
    padding-left: 0;
    width: 40px;
    .circle-add {
      margin-top: 5px;
      display: inline-block;
      height: 20px;
      width: 20px;
      cursor: pointer;
      background: url('../../../../static/plus.svg') center center no-repeat;
    }
  }
  .icon-remove {
    max-width: 100px;
    width: 60px;
    .hold-delete {
      width: 18px;
    }
  }
  .invite-contractor-user {
    max-width: 80px;
    a {
      width: 60px;
      font-weight: bold;
      color: #1779ba;
      text-decoration: underline;
      text-transform: capitalize;
    }
    .hold-invite {
      width: 60px;
    }
  }
  .add-contact {
    margin-top: 10px;
    margin-bottom: 15px;
    font-style: italic;
    cursor: pointer;
    text-decoration: underline;
  }
}
</style>

<style lang="scss">
.contractor-contact-wrapper .contractor-contacts {

  .input-field .text-input input {
    width: 100%;
    margin: 0;
  }

  .contact-name {
    font-size: 0;
    &>span {
      font-size: 14px;
    }
    .input-field {
      font-size: 14px;
      width: 50%;
      display: inline-block;
      .text-input {
        margin: 0;
      }
    }
    .input-field:first-of-type {
      vertical-align: bottom;
    }
  }
}
</style>
